import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ScoreBadge } from "@/components/ScoreBadge";
import LeadDrawer from "@/components/LeadDrawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Pipeline() {
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    const [m, l] = await Promise.all([api.get("/meta"), api.get("/leads")]);
    setStages(m.data.stages);
    setLeads(l.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const id = searchParams.get("lead");
    if (id) setOpenLeadId(id);
  }, [searchParams]);

  const changeStage = async (lead, newStage) => {
    if (newStage === lead.status) return;
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { status: newStage });
      setLeads((prev) => prev.map((x) => (x.id === lead.id ? data : x)));
      toast.success(`Moved to ${newStage}`);
    } catch (err) {
      toast.error("Failed to update stage");
    }
  };

  const updateLeadLocal = (updated) => {
    setLeads((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
  };
  const removeLeadLocal = (id) => setLeads((prev) => prev.filter((x) => x.id !== id));

  const grouped = stages.map((s) => ({ stage: s, items: leads.filter((l) => l.status === s) }));

  return (
    <AppShell>
      <PageHeader
        eyebrow="CRM Pipeline"
        title="Kanban board"
        subtitle="Move leads through 11 forge stages. Click a card to open audit + outreach controls."
        testid="pipeline-header"
      >
        <Button
          data-testid="pipeline-add-lead-btn"
          onClick={() => setOpenLeadId("__new__")}
          variant="outline"
          className="border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-sm"
        >
          <Plus size={16} className="mr-2" /> Add lead
        </Button>
      </PageHeader>

      <div className="px-4 md:px-10 py-6">
        <div
          data-testid="kanban-board"
          className="flex gap-3 overflow-x-auto pb-4 items-start"
          style={{ minHeight: "70vh" }}
        >
          {grouped.map((g) => (
            <div
              key={g.stage}
              data-testid={`column-${g.stage}`}
              className="min-w-[300px] w-[300px] bg-zinc-950/60 border border-zinc-800 rounded-sm flex flex-col max-h-[78vh]"
            >
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-800 bg-zinc-900/70 sticky top-0">
                <div className="flex items-center gap-2">
                  <span className="font-display font-medium text-sm text-zinc-200">{g.stage}</span>
                  <span className="font-mono text-[10px] text-orange-500/80 bg-orange-500/10 border border-orange-500/30 px-1.5 rounded-sm">
                    {g.items.length}
                  </span>
                </div>
              </div>
              <div className="p-2 flex flex-col gap-2 overflow-y-auto flex-1">
                {g.items.length === 0 ? (
                  <div className="px-2 py-6 text-center text-[11px] text-zinc-600 font-mono">empty</div>
                ) : (
                  g.items.map((l) => (
                    <div
                      key={l.id}
                      data-testid={`lead-card-${l.id}`}
                      className="bg-zinc-900 border border-zinc-800 hover:border-orange-500/60 p-3 rounded-sm group transition-colors"
                    >
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setOpenLeadId(l.id);
                          setSearchParams({ lead: l.id });
                        }}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="text-sm text-zinc-100 font-medium truncate">{l.business_name}</div>
                          <ScoreBadge score={l.lead_score} size="sm" />
                        </div>
                        <div className="text-[11px] text-zinc-500 font-mono truncate">
                          {l.industry || "—"} · {l.city || "—"}
                        </div>
                        {l.pain_points && (
                          <div className="text-[11px] text-zinc-400 mt-1.5 line-clamp-2">{l.pain_points}</div>
                        )}
                      </div>
                      <div className="mt-2.5 pt-2 border-t border-zinc-800">
                        <Select value={l.status} onValueChange={(v) => changeStage(l, v)}>
                          <SelectTrigger
                            data-testid={`lead-card-${l.id}-stage`}
                            className="h-7 text-[11px] bg-zinc-950 border-zinc-800 text-zinc-300"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-800">
                            {stages.map((s) => (
                              <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <LeadDrawer
        leadId={openLeadId}
        stages={stages}
        onClose={() => {
          setOpenLeadId(null);
          setSearchParams({});
        }}
        onUpdated={updateLeadLocal}
        onCreated={(l) => setLeads((prev) => [l, ...prev])}
        onDeleted={removeLeadLocal}
      />
    </AppShell>
  );
}
